import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';
import { fetchGlobalSearch, type GlobalSearchData } from './api';
import {
  GlobalSearchLoading,
  GlobalSearchPlaceholderShort,
  GlobalSearchPlaceholderStart,
  GlobalSearchResultsView,
  type GlobalFilter,
} from './GlobalSearchResults';
import { useDebouncedValue } from './useDebouncedValue';

const svgSearchIcon =
  'M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z';

type QueryValue =
  | { kind: 'ok'; data: GlobalSearchData }
  | { kind: 'miss'; message: string };

export function GlobalSearchModalMount(): ReactElement {
  const [raw, setRaw] = useState('');
  const debounced = useDebouncedValue(raw.trim(), 400);
  const [activeFilter, setActiveFilter] = useState<GlobalFilter>('all');

  const enabled = debounced.length >= 2;

  const q = useQuery({
    queryKey: ['rnm', 'global', 'modal', debounced],
    queryFn: async (): Promise<QueryValue> => {
      const r = await fetchGlobalSearch(debounced, { reloadOnPersistentNonce: true });
      if (r.ok) {
        return { kind: 'ok', data: r.data };
      }
      return { kind: 'miss', message: r.message };
    },
    enabled,
  });

  useEffect(() => {
    function onClosed(): void {
      setRaw('');
      setActiveFilter('all');
    }
    window.addEventListener('rnm-global-search-modal-closed', onClosed);
    return () => window.removeEventListener('rnm-global-search-modal-closed', onClosed);
  }, []);

  let results: ReactElement;
  if (!enabled) {
    if (debounced.length === 1) {
      results = <GlobalSearchPlaceholderShort />;
    } else {
      results = <GlobalSearchPlaceholderStart />;
    }
  } else if (q.isFetching && !q.data) {
    results = <GlobalSearchLoading />;
  } else if (q.isError) {
    results = (
      <div className="rnm-error">
        <p>Error al buscar. Por favor intenta nuevamente o recarga la página.</p>
      </div>
    );
  } else if (q.data?.kind === 'miss') {
    results = (
      <div className="rnm-no-results">
        <svg width="48px" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
          <path
            fill="#999"
            d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"
          />
        </svg>
        <p dangerouslySetInnerHTML={{ __html: q.data.message }} />
      </div>
    );
  } else if (q.data?.kind === 'ok') {
    results = (
      <GlobalSearchResultsView
        data={q.data.data}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
      />
    );
  } else {
    results = <GlobalSearchLoading />;
  }

  const hasText = raw.trim().length > 0;

  return (
    <>
      <div className="rnm-search-header">
        <button
          type="button"
          className="rnm-modal-close"
          id="rnm-global-search-close"
          aria-label="Cerrar búsqueda"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className="rnm-search-input-wrapper">
          <svg className="rnm-search-icon" width="20px" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
            <path d={svgSearchIcon} />
          </svg>
          <input
            type="text"
            id="rnm-global-search-input"
            className="rnm-search-input"
            placeholder="Buscar productos, posts, páginas..."
            autoComplete="off"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
          />
          <button
            type="button"
            className="rnm-search-clear"
            id="rnm-search-clear"
            style={{ display: hasText ? 'block' : 'none' }}
            aria-label="Limpiar búsqueda"
            onClick={() => {
              setRaw('');
            }}
          >
            &times;
          </button>
        </div>
      </div>
      <div id="rnm-global-search-results" className="rnm-global-search-results">
        {results}
      </div>
    </>
  );
}
